import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { nbaToday } from "@/lib/format";
import { DataPipelineDashboard } from "@/components/settings/DataPipelineDashboard";
import { UsersPanel } from "@/components/settings/UsersPanel";
import { SubscribersPanel } from "@/components/settings/SubscribersPanel";
import { ChangePassword } from "@/components/settings/ChangePassword";
import { MarginsCard } from "@/components/teamproj/MarginsCard";
import { BandSettingsCard } from "@/components/settings/BandSettingsCard";
import { CalibrationCard } from "@/components/settings/CalibrationCard";
import { RotationBoundsCard } from "@/components/settings/RotationBoundsCard";
import { GarbagePointsCard } from "@/components/settings/GarbagePointsCard";
import { RebuildRatingsCard } from "@/components/settings/RebuildRatingsCard";
import { X4RulesCard } from "@/components/settings/X4RulesCard";

// POST jobs by task name → real authorized backend endpoints only.
const JOB_PATH = { update: "/jobs/update", backfill: "/jobs/backfill", starters: "/jobs/starters" };

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get("/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);

  const running = status && Object.values(status.jobs || {}).some((j) => j.running);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const onTask = async (task) => {
    setPending(task);
    try {
      if (JOB_PATH[task]) {
        const { data } = await api.post(JOB_PATH[task]);
        toast[data.started ? "success" : "info"](data.message);
      } else if (task === "lineups") {
        const { data } = await api.post("/lineups/refresh", null, { params: { day: nbaToday() } });
        toast.success(`Lineups refreshed${data?.updated != null ? ` · ${data.updated} updated` : ""}`);
      } else {
        toast.error("This action has no authorized backend endpoint.");
      }
      load();
    } catch (e) { toast.error(errorText(e)); } finally { setPending(null); }
  };

  const onExport = async ({ team }) => {
    const r = await api.get("/export/player-games.csv", { params: { team }, responseType: "blob" });
    const name = /filename="(.+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `${team}_player_games.csv`;
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DataPipelineDashboard status={status} logs={logs} teams={teams} pending={pending} onTask={onTask} onExport={onExport}>
      <p className="nc-section-label">Administration</p>
      <UsersPanel />
      <SubscribersPanel />
      <p className="nc-section-label">Model &amp; account settings</p>
      <MarginsCard />
      <RebuildRatingsCard />
      <CalibrationCard />
      <RotationBoundsCard />
      <GarbagePointsCard />
      <BandSettingsCard />
      <X4RulesCard />
      <ChangePassword />
      <p className="nc-muted" style={{ marginTop: 18 }}>Automatic runs: 08:00 and 10:00 Europe/Athens (retry of incomplete box scores), plus a 15-minute slate watcher for lineups and live scores. Data source: ESPN public scoreboard &amp; summary endpoints.</p>
    </DataPipelineDashboard>
  );
}
